import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import GoogleAuth from "../../components/frontend/auth/GoogleAuth";
import GoogleRegistrationForm from "../../components/frontend/auth/GoogleRegistrationForm";
import ManualRegisterForm from "../../components/frontend/auth/ManualRegisterForm";
import { useAuthActions } from "../../hooks/useAuthActions";
import { useAuthNavigation } from "../../hooks/useAuthNavigation";

const RegisterPage: React.FC = () => {
  const { t: myTranslate } = useTranslation();
  const { register, googleAuth, googleRegister, isLoading, errorMessage } =
    useAuthActions();
  const { navigateToLogin, navigateToDestination } = useAuthNavigation();

  // State for Google registration flow
  const [showGoogleRegistration, setShowGoogleRegistration] = useState(false);
  const [googleData, setGoogleData] = useState<{
    idToken: string;
    googleUser: {
      email: string;
      firstName: string;
      lastName: string;
      profilePicture?: string;
    };
  } | null>(null);

  // State for registration success
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Handle successful manual registration
  useEffect(() => {
    if (register.isSuccess) {
      // Show success message with email verification info
      const message =
        register.data?.data?.message ||
        "Registration successful! Please check your email to verify your account.";
      setSuccessMessage(message);
      setRegistrationSuccess(true);
    }
  }, [register.isSuccess, register.data]);

  // Handle successful Google auth
  useEffect(() => {
    if (googleAuth.isSuccess && googleAuth.data?.data) {
      const data = googleAuth.data.data;

      if (data.requiresEmailLogin) {
        // User exists with password - redirect to login
        navigateToLogin();
        return;
      }

      if (data.isNewUser) {
        // Show Google registration form for new users
        setShowGoogleRegistration(true);
      } else {
        // Existing user - log them in and redirect
        navigateToDestination();
      }
    }
  }, [googleAuth.isSuccess, googleAuth.data]);

  // Handle successful Google registration
  useEffect(() => {
    if (googleRegister.isSuccess) {
      navigateToDestination();
    }
  }, [googleRegister.isSuccess]);

  // Handle manual registration form submission
  const handleManualRegister = (data: any) => register.mutateAsync(data);

  // Handle Google auth
  const handleGoogleAuth = (payload: {
    data: any;
    type: "login" | "register";
  }) => {
    if (payload.type === "register") {
      // Decode Google token to get user info
      try {
        const decodedPayload = JSON.parse(
          atob(payload.data.credential.split(".")[1])
        );

        const googleUser = {
          email: decodedPayload.email,
          firstName: decodedPayload.given_name,
          lastName: decodedPayload.family_name,
          profilePicture: decodedPayload.picture,
        };

        setGoogleData({
          idToken: payload.data.credential,
          googleUser,
        });

        // Authenticate with backend
        googleAuth.mutate({ idToken: payload.data.credential });
      } catch (error) {
        console.error("Failed to decode Google credential:", error);
      }
    }
  };

  const handleGoogleError = () => {
    // Error is handled by the hook
  };

  // Handle Google registration form submission
  const handleGoogleRegistration = (data: any) => {
    if (googleData) {
      googleRegister.mutate({
        idToken: googleData.idToken,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
      });
    }
  };

  // Handle Google registration cancel
  const handleGoogleRegistrationCancel = () => {
    setShowGoogleRegistration(false);
    setGoogleData(null);
  };

  // Show Google registration form
  if (showGoogleRegistration && googleData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 2,
        }}
      >
        <GoogleRegistrationForm
          googleUser={googleData.googleUser}
          onSubmit={handleGoogleRegistration}
          isLoading={isLoading}
          error={errorMessage}
          onCancel={handleGoogleRegistrationCancel}
        />
      </Box>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show registration success state
  if (registrationSuccess) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            maxWidth: 400,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            className="notranslate"
            translate="no"
            gutterBottom
            align="center"
            sx={{ mb: 3 }}
          >
            {myTranslate("auth.registrationSuccessful")}
          </Typography>

          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>

          <Typography
            variant="body2"
            className="notranslate"
            translate="no"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            {myTranslate("auth.checkEmailVerification")}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            className="notranslate"
            translate="no"
            onClick={navigateToLogin}
            sx={{ fontWeight: 600, py: 1.2 }}
          >
            {myTranslate("auth.goToLogin")}
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          className="notranslate"
          translate="no"
          gutterBottom
          align="center"
          sx={{ mb: 3 }}
        >
          {myTranslate("auth.createAccount")}
        </Typography>

        <Typography
          variant="body2"
          className="notranslate"
          translate="no"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {myTranslate("auth.joinUsToStart")}
        </Typography>

        {/* Error messages are handled by individual components */}

        {/* Google Auth */}
        <GoogleAuth
          type="register"
          onSuccess={handleGoogleAuth}
          onError={handleGoogleError}
        />

        <Divider sx={{ my: 3 }}>
          <Box
            component="span"
            className="notranslate"
            translate="no"
            sx={{ color: "text.secondary" }}
          >
            {myTranslate("auth.or")}
          </Box>
        </Divider>

        {/* Manual Registration Form */}
        <ManualRegisterForm
          onSubmit={handleManualRegister}
          isLoading={isLoading}
          error={errorMessage}
          onLogin={navigateToLogin}
        />
      </Paper>
    </Box>
  );
};

export default RegisterPage;
